<template>
  <el-menu
    :default-active="activeIndex"
    class="navbar-menu"
    mode="horizontal"
    :ellipsis="false"
    background-color="#9dbcc4"
    text-color="#303133"
    active-text-color="#156d7b"
    @select="handleSelect"
  >
    <el-menu-item index="home" class="logo-item">
      <img src="../assets/logo2.jpeg" alt="Wedoomotion" class="logo-img" />
    </el-menu-item>

    <div class="flex-grow" />

    <el-menu-item index="rubriques" title="Rubriques">
      <el-icon><Grid /></el-icon><span class="menu-label">Rubriques</span>
    </el-menu-item>
    <el-menu-item index="notifications" title="Notifications">
      <el-badge :value="unreadCount" :hidden="!unreadCount" :max="99" class="notif-badge">
        <el-icon><Bell /></el-icon>
      </el-badge>
      <span class="menu-label">Notifications</span>
    </el-menu-item>
    <el-menu-item index="messages" title="Messages">
      <el-icon><ChatDotRound /></el-icon><span class="menu-label">Messages</span>
    </el-menu-item>
    <el-menu-item index="friends" title="Amis">
      <el-icon><User /></el-icon><span class="menu-label">Amis</span>
    </el-menu-item>
    <el-menu-item index="search" title="Rechercher">
      <el-icon><Search /></el-icon><span class="menu-label">Rechercher</span>
    </el-menu-item>
    <el-menu-item v-if="isStaff" index="moderation" title="Modération">
      <el-icon><Warning /></el-icon><span class="menu-label">Modération</span>
    </el-menu-item>

    <div class="flex-grow" />

    <template v-if="!isLoggedIn">
      <el-menu-item index="login" title="Connexion">
        <el-icon><Key /></el-icon><span class="menu-label">Connexion</span>
      </el-menu-item>
      <el-menu-item index="register" title="Inscription">
        <el-icon><EditPen /></el-icon><span class="menu-label">Inscription</span>
      </el-menu-item>
    </template>
    <template v-else>
      <el-menu-item index="logout" title="Déconnexion">
        <el-icon><SwitchButton /></el-icon><span class="menu-label">Déconnexion</span>
      </el-menu-item>
    </template>
  </el-menu>
</template>

<script>
import api from '@/api';
import {
  Grid, Bell, ChatDotRound, User, Search, Warning, Key, EditPen, SwitchButton,
} from '@element-plus/icons-vue';

// Rafraîchissement du compteur de notifications non lues (ms)
const UNREAD_POLL_INTERVAL = 30000;

export default {
  name: "NavigationBar",
  components: {
    Grid, Bell, ChatDotRound, User, Search, Warning, Key, EditPen, SwitchButton,
  },

  data() {
    return {
      isLoggedIn: false,
      userStatus: null,
      unreadCount: 0,
      unreadInterval: null,
    };
  },

  computed: {
    // Lien « Modération » réservé aux ADMIN et MODERATOR
    isStaff() {
      return this.isLoggedIn && (this.userStatus === 'ADMIN' || this.userStatus === 'MODERATOR');
    },

    // Item actif d'après l'URL (comparaison insensible à la casse : /Rubriques et /rubriques)
    activeIndex() {
      const path = (this.$route.path || '').toLowerCase();
      const map = {
        '/': 'home',
        '/rubriques': 'rubriques',
        '/notifications': 'notifications',
        '/messages': 'messages',
        '/friends': 'friends',
        '/chercher-utilisateur': 'search',
        '/moderation': 'moderation',
        '/login': 'login',
        '/register': 'register',
      };
      if (map[path]) return map[path];
      // Une conversation fait partie de la messagerie
      if (path.startsWith('/conversation/')) return 'messages';
      return '';
    },
  },

  methods: {
    async logout() {
      try {
        await api.post('/session/logout');
        this.isLoggedIn = false;
        window.location.reload(true);
      } catch (error) {
        console.error(error);
      }
    },

    async checkUserSession() {
      try {
        const response = await api.get('/session/me');

        this.isLoggedIn = response.status === 200;
        this.userStatus = this.isLoggedIn && response.data ? response.data.status : null;
      } catch (error) {
        console.error('Error checking session:', error);
        this.isLoggedIn = false;
        this.userStatus = null;
      }
    },

    async fetchUnreadCount() {
      try {
        const response = await api.get('/user/notifications/unread-count');
        this.unreadCount = (response && response.data && response.data.count) || 0;
      } catch (error) {
        // Compteur non bloquant : on garde la dernière valeur connue
      }
    },

    handleSelect(index) {
      if (index === 'logout') {
        this.logout();
        return;
      }

      const routes = {
        home: '/',
        rubriques: '/Rubriques',
        notifications: this.isLoggedIn ? '/Notifications' : '/register',
        messages: this.isLoggedIn ? '/Messages' : '/register',
        friends: this.isLoggedIn ? '/Friends' : '/register',
        search: this.isLoggedIn ? '/chercher-utilisateur' : '/register',
        moderation: '/moderation',
        login: '/login',
        register: '/register',
      };

      if (routes[index]) {
        this.$router.push(routes[index]);
      }
    },
  },

  async mounted() {
    await this.checkUserSession();
    if (this.isLoggedIn) {
      await this.fetchUnreadCount();
      this.unreadInterval = setInterval(this.fetchUnreadCount, UNREAD_POLL_INTERVAL);
    }
  },

  beforeUnmount() {
    clearInterval(this.unreadInterval);
  },
};
</script>

<style scoped>
.navbar-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  padding: 0 20px;
  border-bottom: none;
  box-sizing: border-box;
}

.logo-item {
  padding: 0 !important;
}

.logo-img {
  height: 40px;
  margin: 0 10px;
  display: block;
}

.flex-grow {
  flex-grow: 1;
}

.notif-badge {
  display: inline-flex;
  margin-right: 5px;
}

.notif-badge :deep(.el-icon) {
  margin-right: 0;
}

.notif-badge :deep(.el-badge__content) {
  top: 14px;
}

/* Uniformiser la police sur tous les items de la navbar */
.navbar-menu :deep(.el-menu-item) {
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
  font-size: 14px;
}

.navbar-menu :deep(.el-menu-item.is-active) {
  font-family: 'Helvetica Neue', Helvetica, 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', Arial, sans-serif;
  font-size: 14px;
}

/* Petits écrans : icônes seules (le libellé reste en infobulle via title) */
@media (max-width: 900px) {
  .navbar-menu {
    padding: 0 4px;
  }

  .menu-label {
    display: none;
  }

  .navbar-menu :deep(.el-menu-item) {
    padding: 0 10px;
  }

  .navbar-menu :deep(.el-menu-item .el-icon) {
    margin-right: 0;
  }
}
</style>
